import { useEffect, useMemo } from "react";
import { useLocation } from "react-router-dom";
import { useCustomNavigation } from "@/hooks/useCustomNavigation";
import { AnimatedFigureIcon } from "@/Components/AvatarLifeCycle";
import {
  clearAvatarSharingData,
  getAvatarSharingData,
} from "@/Utils/avatarSharingStorage";
import { resetWidgetSession } from "@/Utils/widgetSession";
import RedirectProgress from "@/Components/RedirectProgress";

import "./shareCancelled.scss";

// Neutral "cancelled" mark for the status row. Mirrors SendMeasurements'
// green CheckIcon geometry (12×12 viewBox, rounded caps) but draws an ✕ in the
// muted grey used across this screen — this is a user-initiated cancel, not an
// error, so nothing here is red.
const CancelIcon = () => (
  <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
    <path
      d="M3.2 3.2 L8.8 8.8 M8.8 3.2 L3.2 8.8"
      stroke="#8A8A97"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

// Shown after the user confirms "Yes, cancel" on an active store share. Copies
// the SendMeasurements success layout, re-skinned to a neutral palette, then
// hands off to RedirectProgress to return the user to the store (redirectUrl).
const ShareCancelled = () => {
  const { navigateTo } = useCustomNavigation();
  const location = useLocation();
  // This screen serves two exits that both cancel the share and return to the
  // store: a plain "Yes, cancel" (no scan taken) and a discard (a scan was
  // taken, then discarded). They differ only in copy — the discard case is
  // routed here with `state.discarded` so the user is told the avatar was
  // discarded, per the design (the warning/outcome is about the avatar, not
  // about "cancelling measurements").
  const discarded = Boolean((location.state as { discarded?: boolean })?.discarded);

  // Capture org name + redirect URL + session id once, on mount — the 500ms
  // cleanup below wipes the sharing data, and RedirectProgress keeps its own
  // captured copy. orgName falls back to the default brand when there's no
  // sharing session (mirrors SendMeasurements).
  const { orgName, redirectUrl, sessionId } = useMemo(() => {
    const data = getAvatarSharingData();
    return {
      orgName: data.orgName || "TrueToForm",
      redirectUrl: data.redirectUrl || null,
      sessionId: data.sessionId || null,
    };
  }, []);

  useEffect(() => {
    // Reset the widget session so the store treats a re-entry as a fresh
    // session rather than the cancelled one (best-effort, non-blocking).
    resetWidgetSession(sessionId);

    // Wait to display the orgName first, then clear the sharing data (after
    // RedirectProgress has captured the redirectUrl into its own ref).
    const timer = setTimeout(() => {
      clearAvatarSharingData();
    }, 500);
    return () => clearTimeout(timer);
  }, [sessionId]);

  const handleClose = () => {
    navigateTo("/");
  };

  return (
    <div className="share-cancelled">
      <div className="share-cancelled__main">
        {/*
          Neutral (not green, not red) figure — a calm, fully-desaturated state
          that reads as "inactive/cancelled" without alarming the user.
        */}
        <div className="share-cancelled__figure">
          <AnimatedFigureIcon color="#9A9AA8" fillPercent={0} />
        </div>

        <h1 className="share-cancelled__title">
          {discarded ? "Avatar discarded" : "Sharing cancelled"}
        </h1>
        <p className="share-cancelled__subtitle">
          {`Taking you back to ${orgName} shortly.`}
        </p>

        <div className="share-cancelled__status">
          <div className="share-cancelled__status-row">
            <span className="share-cancelled__status-icon">
              <CancelIcon />
            </span>
            <span className="share-cancelled__status-label">
              {discarded ? (
                "Avatar discarded"
              ) : (
                <>
                  Cancelled sending to <b>{orgName}</b>
                </>
              )}
            </span>
          </div>
        </div>
      </div>

      <div className="share-cancelled__footer">
        {redirectUrl ? (
          // Slim redirect bar + "Continue now" — reuses SubmitScan's minimal
          // variant (iOS-safe redirect logic), bound to a short countdown.
          // `cancelled` adds ttf-cancelled=true so the store widget doesn't treat
          // this return as a completed scan (no auto-open / "scan shared").
          <div className="share-cancelled__footer-redirect">
            <RedirectProgress variant="minimal" durationSeconds={3} cancelled />
          </div>
        ) : (
          // No redirect URL (opened outside the widget flow): offer a way out.
          <button
            type="button"
            className="share-cancelled__close"
            onClick={handleClose}
          >
            Close
          </button>
        )}
      </div>
    </div>
  );
};

export default ShareCancelled;
